import {
  obterIdTca,
  obterIdEmpresaReal,
  obterNomeEmpresa,
  obterDocumentoEmpresa,
} from "../utils/empresa.js";

/**
 * Conversores entre o payload bruto do backend e o modelo simplificado
 * usado pela UI de Acompanhamento.
 */

const CHAVES_EMPRESA_EM_ITEM = ["empresa", "cadastro", "estudante"];

const CAMPOS_CNPJ_DIRETO = [
  "cnpj",
  "CNPJ",
  "cnpj_ou_cpf",
  "cnpj_empresa",
];

function extrairEmpresa(item) {
  if (!item || typeof item !== "object") return null;
  for (const chave of CHAVES_EMPRESA_EM_ITEM) {
    if (item[chave] && typeof item[chave] === "object") return item[chave];
  }
  return item;
}

/**
 * Lê um campo tolerando diferentes formatos do backend, incluindo
 * variações que vieram dentro de `raw.cnpj_empresa`, `raw.cnpj`,
 * `raw.empresa.cnpj`, `raw.estudante.cnpj`, etc.
 */
function obterCampoCnpj(item, fonteEmpresa) {
  if (!item || typeof item !== "object") return "";

  // 1) Procura primeiro no item (top-level)
  for (const campo of CAMPOS_CNPJ_DIRETO) {
    const valor = item[campo];
    if (typeof valor === "string" && valor.trim()) return valor.trim();
  }

  // 2) Depois, dentro do objeto empresa/estudante embutido
  if (fonteEmpresa && typeof fonteEmpresa === "object") {
    for (const campo of CAMPOS_CNPJ_DIRETO) {
      const valor = fonteEmpresa[campo];
      if (typeof valor === "string" && valor.trim()) return valor.trim();
    }
  }

  return "";
}

/**
 * Normaliza um item de acompanhamento para o formato:
 *   {
 *     id,                    // TcaEmpregador.id (preferência) — usado em
 *                            // /tca-empregador/:id/status-completo
 *     tca_id,                // alias explícito do mesmo id
 *     empresa_id,            // Empresa.id real (JOIN) — usado para abrir
 *                            // detalhe da Empresa, NUNCA do TCA
 *     nome,
 *     documento,
 *     cnpj,                  // CNPJ/CPF preferencial (vem do top-level do item)
 *     cnpj_empresa,          // CNPJ da empresa vinculada (vem do JOIN Empresa)
 *     cnpj_ou_cpf,           // alias legado do backend
 *     tipo_programa_empresa,
 *     raw
 *   }
 * Retorna null quando o item não tem ID válido.
 */
export function normalizarEmpresaAcompanhamentoItem(item) {
  if (!item || typeof item !== "object") return null;

  const fonteEmpresa = extrairEmpresa(item);

  // id do TCA Empregador (preferência) — usado pelo /status-completo.
  // `obterIdTca` prioriza `tca_id` / `tcaId` / `tca_empregador_id` sobre
  // o `id` no top-level, evitando o falso 404 quando o backend passou
  // a devolver o id do TCA no campo `tca_id` em vez de `id`.
  const idTca = obterIdTca(item);
  if (!idTca) return null;

  // id real da Empresa (vem do JOIN). Fica exposto em `empresa_id` para
  // quando a chamada precisar do detalhe da Empresa, não do TCA.
  const idEmpresa = obterIdEmpresaReal(item) || idTca;

  const nome = obterNomeEmpresa(fonteEmpresa) || obterNomeEmpresa(item);
  const documento =
    obterDocumentoEmpresa(fonteEmpresa) || obterDocumentoEmpresa(item);

  // CNPJ/CNPJ-empresa resolvido em camadas (top-level → empresa embarcada → raw)
  const cnpjDireto = obterCampoCnpj(item, fonteEmpresa);
  const cnpjDoRaw =
    item?.cnpj_empresa ||
    item?.empresa?.cnpj ||
    item?.cadastro?.cnpj ||
    item?.estudante?.cnpj ||
    "";

  return {
    id: idTca,
    tca_id: idTca,
    empresa_id: idEmpresa,
    nome,
    documento,
    cnpj: cnpjDireto || cnpjDoRaw,
    cnpj_empresa: item?.cnpj_empresa || fonteEmpresa?.cnpj || "",
    cnpj_ou_cpf: item?.cnpj_ou_cpf || "",
    tipo_programa_empresa:
      item?.tipo_programa_empresa ||
      fonteEmpresa?.tipo_programa_empresa ||
      fonteEmpresa?.TIPOPROGRAMA ||
      "",
    raw: item,
  };
}

export function deduplicarAcompanhamentosPorEmpresa(lista) {
  if (!Array.isArray(lista)) return [];

  // A deduplicação é por id do TCA Empregador (não da Empresa), porque
  // é o que identifica unicamente um contrato de aprendizagem.
  const mapa = new Map();
  for (const item of lista) {
    const id = item?.tca_id ?? item?.id;
    if (!id) continue;
    if (!mapa.has(id)) mapa.set(id, item);
  }
  return Array.from(mapa.values());
}
